import { useState, type FC, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { CheckmarkOutline, CloseOutline, Information } from '@mint-lib/icons';
import { useProfile } from '@mint-lib/profile-helpers';
import { useQueryClient } from '@mint-lib/query-client';
import {
  Button,
  Chip,
  Divider,
  typographyVariants,
} from '@mint-lib/tw-components';
import { ApprovalFlows } from '@mint-sdk/api-approval-flows';

import useFlowProcessDetailsData from '../../api/queries/useFlowProcessDetailsData.js';
import { EntitySpecificFields } from '../../types/shared.js';
import ApproverDecisionModal from '../modals/ApproverDecisionModal.jsx';
import StatusDetailsModal from '../modals/StatusDetailsModal/StatusDetailsModal.jsx';

type Props = EntitySpecificFields & {
  approvalProcessId: string | null;
};

type ModalTypes = 'approve-request' | 'reject-request' | 'status-details';

const ApproverPending: FC<Props> = ({
  approvalProcessId,
  entityType,
  entityId,
  entitySource,
}) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');
  const queryClient = useQueryClient();
  const [activeModal, setActiveModal] = useState<ModalTypes | null>(null);

  const isApproverDecisionModalOpen =
    activeModal === 'approve-request' || activeModal === 'reject-request';
  const isStatusDetailsModalOpen = activeModal === 'status-details';

  const { data: flowProcessData } = useFlowProcessDetailsData({
    enabled: true,
    approvalProcessId,
  });
  const { profile } = useProfile();
  const currentUserId = profile?.id;

  const isDecisionActionRequired = useMemo(() => {
    if (
      !flowProcessData ||
      flowProcessData.activeStageIndex === -1 ||
      !currentUserId
    ) {
      return false;
    }

    const activeStage =
      flowProcessData.stages[flowProcessData.activeStageIndex];

    return activeStage.approvers.some((approverItem) => {
      if (approverItem.status !== 'pending') {
        return false;
      }

      return approverItem.candidates.some((candidate) => {
        if (candidate.status !== 'pending') {
          return false;
        }

        return candidate.userId === currentUserId;
      });
    });
  }, [flowProcessData, currentUserId]);

  const showApproverDecisionApproveModal = () => {
    setActiveModal('approve-request');
  };

  const showApproverDecisionRejectModal = () => {
    setActiveModal('reject-request');
  };

  const showStatusDetailsModal = () => {
    setActiveModal('status-details');
  };

  const closeActiveModal = () => {
    setActiveModal(null);
  };

  const getApproverDecisionModalVariant = () => {
    if (activeModal === 'approve-request') {
      return 'approve';
    }

    if (activeModal === 'reject-request') {
      return 'reject';
    }

    return null;
  };
  const modalVariant = getApproverDecisionModalVariant();

  const handleAfterDecisionAction = () => {
    if (!approvalProcessId || modalVariant !== 'approve') {
      return;
    }

    ApprovalFlows.checkFlowStatus.invalidate(queryClient, {
      params: {
        entityId,
        entitySource,
        entityType,
      },
    });
    ApprovalFlows.getRequest.invalidate(queryClient, {
      urlParams: { requestId: approvalProcessId },
    });
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2">
          <span
            className={typographyVariants({
              variant: 'caption1',
              color: 'secondary',
            })}
          >
            {t('Status')}:
          </span>

          <Chip
            className="cursor-pointer"
            variant="outline"
            color="info"
            size="medium"
            onClick={showStatusDetailsModal}
          >
            <Information />
            {t('Pending')}
          </Chip>
        </div>
        {isDecisionActionRequired ? (
          <>
            <Divider orientation="vertical" className="max-h-6" />
            <Button
              data-dd-action-name="Show rejection modal"
              variant="outlined"
              color="error"
              size="medium"
              onClick={showApproverDecisionRejectModal}
            >
              <CloseOutline className="fill-error-main" />
              {t('Reject')}
            </Button>
            <Button
              data-dd-action-name="Show approval modal"
              variant="contained"
              color="primary"
              size="medium"
              onClick={showApproverDecisionApproveModal}
            >
              <CheckmarkOutline className="fill-primary-contrastText" />
              {t('Approve')}
            </Button>
          </>
        ) : null}
      </div>
      <ApproverDecisionModal
        variant={modalVariant}
        flowType={entityType}
        processId={approvalProcessId}
        open={isApproverDecisionModalOpen}
        onClose={closeActiveModal}
        onDecisionSuccess={handleAfterDecisionAction}
      />
      <StatusDetailsModal
        open={isStatusDetailsModalOpen}
        onClose={closeActiveModal}
        approvalProcessId={approvalProcessId}
        userStatus="approver"
      />
    </>
  );
};

export default ApproverPending;
